import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tokens } from '../../theme/tokens';

export default function AssetForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    type: '',
    brand: '',
    serial_no: '',
    asset_value: '',
    description: '',
    purchase_date: '',
    location: '',
  });

  useEffect(() => {
    fetchCategories();
    if (id) {
      fetchAsset();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/assets/categories');
      setCategories(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch categories');
    }
  };

  const fetchAsset = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/assets/${id}`);
      const asset = response.data.data;
      setFormData({
        name: asset.name || '',
        category_id: asset.category_id || '',
        type: asset.type || '',
        brand: asset.brand || '',
        serial_no: asset.serial_no || '',
        asset_value: asset.asset_value || '',
        description: asset.description || '',
        purchase_date: asset.purchase_date ? asset.purchase_date.split('T')[0] : '',
        location: asset.location || '',
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch asset');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = {
        ...formData,
        asset_value: parseFloat(formData.asset_value) || 0,
        category_id: parseInt(formData.category_id),
      };

      if (id) {
        await api.put(`/assets/${id}`, data);
      } else {
        await api.post('/assets', data);
      }
      navigate('/asset');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save asset');
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        crumbs={
          <>
            Operations / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>{id ? 'Asset Detail' : 'New Asset'}</Box>
          </>
        }
        title={id ? 'Asset Detail' : 'New Asset'}
        actions={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/asset')}
            variant="outlined"
            sx={{ borderColor: tokens.border, color: tokens.ink }}
          >
            Assets
          </Button>
        }
      />

      <Paper elevation={0} sx={{ ...surfaceCardSx, p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  label="Category"
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Type"
                name="type"
                value={formData.type}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Serial No"
                name="serial_no"
                value={formData.serial_no}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Asset Value"
                name="asset_value"
                type="number"
                inputProps={{ step: '0.01', min: 0 }}
                value={formData.asset_value}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Purchase Date"
                name="purchase_date"
                type="date"
                value={formData.purchase_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !formData.name || !formData.category_id}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/asset')}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
